import { AttackPattern, PatternState } from '../AttackPattern.js';

let nextFieldToken = 1;

/**
 * Stutter Field (The Unblinking) — leans on BossBullet's `accel` support:
 * a spray of bullets decelerates to a dead stop, littering the arena as a
 * frozen minefield you can walk right up to... then, on a flash cue, every
 * stalled mine re-aims at the player's CURRENT position and relaunches in
 * a staggered ripple. The stall is the telegraph — where you stand when
 * the field wakes decides how survivable the wake is.
 *
 * Holds direct references to its own pooled bullets across the stall (the
 * pool recycles inactive bullets, so each spawn is tagged with a per-cast
 * token and only live bullets still carrying this cast's tag are
 * relaunched — a recycled slot re-fired by a layered pattern is left alone).
 */
export class StutterField extends AttackPattern {
  constructor({ minPhase = 0, config }) {
    super({ name: 'Stutter Field', telegraphDuration: 0.9, activeDuration: 4.4, minPhase });
    this.config = config;
  }

  onBegin() {
    this._token = nextFieldToken++;
    this._bullets = [];
    this._elapsed = 0;
    this._sprayTimer = 0;
    this._sprayed = 0;
    this._queue = null;
    this._relaunchTimer = 0;
  }

  onActiveTick(dt, boss, player, pool) {
    this._elapsed += dt;
    const count = this.config.count + boss.phase * 4;

    // Phase A: spray across the first 1.2s, golden-angle steps for even,
    // unclumped coverage. Negative accel stalls each bullet in ~1.4s.
    if (this._sprayed < count) {
      this._sprayTimer -= dt;
      if (this._sprayTimer <= 0) {
        this._sprayTimer = 1.2 / count;
        const angle = this._sprayed * 2.399963;
        const bullet = pool.fire({
          x: boss.x,
          y: boss.y,
          vx: Math.cos(angle) * this.config.spraySpeed,
          vy: Math.sin(angle) * this.config.spraySpeed,
          radius: 2.8,
          damage: this.config.bulletDamage,
          color: '#8ae06e',
          lifetime: 6,
          accel: -this.config.spraySpeed / 1.4,
        });
        bullet._stutterToken = this._token;
        this._bullets.push(bullet);
        this._sprayed++;
      }
    }

    // Phase B: the wake — snapshot the surviving, still-ours mines and
    // relaunch them at the player in a staggered ripple.
    if (this._queue === null && this._elapsed >= 2.7) {
      this._queue = this._bullets.filter((b) => b.active && b._stutterToken === this._token);
      for (let i = this._queue.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [this._queue[i], this._queue[j]] = [this._queue[j], this._queue[i]];
      }
      this._relaunchTimer = 0;
      this._stagger = this._queue.length ? 0.9 / this._queue.length : 0;
    }

    if (this._queue !== null && this._queue.length) {
      this._relaunchTimer -= dt;
      while (this._relaunchTimer <= 0 && this._queue.length) {
        const bullet = this._queue.pop();
        this._relaunchTimer += this._stagger;
        if (!bullet.active || bullet._stutterToken !== this._token) continue;
        const angle = Math.atan2(player.y - bullet.y, player.x - bullet.x);
        const speed = this.config.relaunchSpeed + boss.phase * 10;
        bullet.accel = 0;
        bullet.vx = Math.cos(angle) * speed;
        bullet.vy = Math.sin(angle) * speed;
        bullet.speed = speed;
        bullet.lifetime = Math.max(bullet.lifetime, 3);
      }
    }
  }

  getVisual(boss) {
    // The pre-wake warning rides the existing 'flash' visual — the frozen
    // mines themselves are the real telegraph.
    if (this.state === PatternState.ACTIVE && this._elapsed >= 2.1 && this._queue === null) {
      return { kind: 'flash', x: boss.x, y: boss.y, color: '#8ae06e' };
    }
    return null;
  }
}
